/**
 * TopologyPanel — Infrastruktura view: kategorizovaný přehled nodů z mesh_topology
 * agregátoru (Filip 2026-07-22). Tři sekce, jasně vizuálně oddělené:
 *   - MESH cluster   = lokální členové (acm.mesh_nodes)
 *   - Remote instance = cizí hub-linked instance (mesh_peers mimo cluster)
 *   - Discovered     = autodiscovery scan, nepřipojené
 * Widget-styl: jeden MCP tool (mesh_topology) → render přes sdílené ui komponenty.
 */
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Cloud, Radar, Server } from 'lucide-react';
import { api } from '../../lib/api';
import { Badge, Card, CardContent, EmptyState, StatCard, StatsGrid, StatusDot } from '../../components/ui';

interface TopoNode {
  id: string;
  label: string;
  host: string | null;
  role: string | null;
  env: string | null;
  commit: string | null;
  announce_age_s: number | null;
  health: string;
  latency_ms: number | null;
  announcing: boolean;
  public_ui?: string | null;
  public_ui_published?: boolean;
}
interface DiscoveredNode {
  id: string;
  hostname: string | null;
  ip: string;
  bootstrap_method: string | null;
  default_credentials_used: boolean;
  first_seen: string | null;
}
interface Topology {
  ok: boolean;
  cluster: TopoNode[];
  remote: TopoNode[];
  discovered: DiscoveredNode[];
  summary: {
    cluster_total: number;
    cluster_online: number;
    remote_total: number;
    discovered_total: number;
  };
}

// Mapuj health na StatusDot slovník (RUNNING/ERROR/BUILDING sety):
// online→green, offline→red, degraded→yellow(pulse), unknown→gray.
function dotStatus(h: string): string {
  if (h === 'online') return 'online';
  if (h === 'offline') return 'error';
  if (h === 'degraded') return 'restarting';
  return 'unknown';
}

function age(s: number | null): string {
  if (s === null) return '—';
  if (s < 90) return `${s}s`;
  if (s < 5400) return `${Math.round(s / 60)}min`;
  return `${Math.round(s / 3600)}h`;
}

function NodeRow({ n, stale }: { n: TopoNode; stale: boolean }) {
  const { t } = useTranslation();
  return (
    <div className='flex items-center gap-3 px-3 py-2 border-b border-gray-800/60 last:border-0'>
      <StatusDot status={dotStatus(n.health)} />
      <div className='min-w-0 flex-1'>
        <div className='flex items-center gap-2'>
          <span className='text-sm font-medium text-white truncate'>{n.label || n.id}</span>
          {n.role && <Badge variant={n.role === 'master' ? 'purple' : 'blue'}>{n.role}</Badge>}
          {n.env && (
            <Badge variant={n.env === 'production' ? 'red' : 'gray'}>{n.env}</Badge>
          )}
          {/* Živý nod bez MQTT announce = probe-only (ne „mrtvý“) */}
          {!n.announcing && (n.health === 'online' || n.health === 'degraded') && (
            <Badge variant='yellow'>{t('mesh.noAnnounce', 'bez announce')}</Badge>
          )}
          {!n.announcing && n.health !== 'online' && n.health !== 'degraded' && (
            <Badge variant='red'>{t('mesh.notAnnouncing', 'neannouncuje')}</Badge>
          )}
        </div>
        <div className='text-[11px] text-gray-500 font-mono truncate'>
          {n.host ?? '—'}
          {n.commit ? ` · ${n.commit}` : ''}
          {n.latency_ms != null ? ` · ${n.latency_ms}ms` : ''}
          {n.public_ui
            ? (
              <span className={n.public_ui_published ? 'text-blue-400' : 'text-amber-400/90'}>
                {' · '}
                {n.public_ui}
                {!n.public_ui_published ? ' (pending)' : ''}
              </span>
            )
            : null}
        </div>
      </div>
      <div className={`text-[11px] shrink-0 ${stale ? 'text-amber-400' : 'text-gray-500'}`}>
        {age(n.announce_age_s)}
      </div>
    </div>
  );
}

export function TopologyPanel() {
  const { t } = useTranslation();
  const { data, isLoading, isError } = useQuery({
    queryKey: ['mesh-topology'],
    queryFn: () => api.mcp<Topology>('mesh.topology'),
    refetchInterval: 15_000,
  });

  if (isLoading) {
    return (
      <div className='text-sm text-gray-500 py-8 text-center'>
        {t('mesh.loadingTopology', 'Načítám topologii…')}
      </div>
    );
  }
  if (isError || !data?.ok) {
    return (
      <div className='text-sm text-red-400 py-8 text-center'>
        {t('mesh.topologyLoadError', 'mesh_topology se nepodařilo načíst.')}
      </div>
    );
  }

  const s = data.summary;
  // Stejný TTL jako peers_list / mesh_topology (PEER_ONLINE_MAX_AGE_S = 180)
  const staleThreshold = 180;

  return (
    <div className='space-y-5'>
      <StatsGrid columns={4}>
        <StatCard icon={Server} label={t('mesh.clusterOnline', 'Cluster online')} value={`${s.cluster_online}/${s.cluster_total}`} variant={s.cluster_online === s.cluster_total ? 'success' : 'warning'} />
        <StatCard icon={Cloud} label={t('mesh.remoteInstances', 'Remote instance')} value={s.remote_total} />
        <StatCard icon={Radar} label={t('mesh.discovered', 'Discovered')} value={s.discovered_total} />
        <StatCard icon={Server} label={t('mesh.nodesTotal', 'Nody celkem')} value={s.cluster_total + s.remote_total} />
      </StatsGrid>

      {/* MESH cluster */}
      <Card>
        <CardContent className='p-0'>
          <div className='flex items-center gap-2 px-3 py-2 border-b border-gray-800'>
            <Server size={14} className='text-emerald-400' />
            <h3 className='text-xs font-semibold text-gray-300 uppercase tracking-wider'>MESH cluster</h3>
            <span className='text-[10px] text-gray-600'>{t('mesh.sectionClusterHintMesh', 'lokální členové (mesh_nodes)')}</span>
          </div>
          {data.cluster.length === 0
            ? <EmptyState title={t('mesh.sectionClusterEmpty', 'Žádné cluster nody')} />
            : data.cluster.map((n) => (
              <NodeRow key={n.id} n={n} stale={(n.announce_age_s ?? 0) > staleThreshold || !n.announcing} />
            ))}
        </CardContent>
      </Card>

      {/* Remote instance — vizuálně oddělené (cizí) */}
      {data.remote.length > 0 && (
        <Card className='border-amber-500/25'>
          <CardContent className='p-0'>
            <div className='flex items-center gap-2 px-3 py-2 border-b border-amber-500/20 bg-amber-500/5'>
              <Cloud size={14} className='text-amber-400' />
              <h3 className='text-xs font-semibold text-amber-300 uppercase tracking-wider'>Remote instance</h3>
              <span className='text-[10px] text-amber-600/80'>{t('mesh.sectionRemoteHintStrong', 'cizí hub-linked — NEjsou součást clusteru')}</span>
            </div>
            {data.remote.map((n) => <NodeRow key={n.id} n={n} stale={false} />)}
          </CardContent>
        </Card>
      )}

      {/* Discovered */}
      {data.discovered.length > 0 && (
        <Card>
          <CardContent className='p-0'>
            <div className='flex items-center gap-2 px-3 py-2 border-b border-gray-800'>
              <Radar size={14} className='text-sky-400' />
              <h3 className='text-xs font-semibold text-gray-300 uppercase tracking-wider'>Discovered</h3>
              <span className='text-[10px] text-gray-600'>{t('mesh.discoveredHint', 'autodiscovery — nepřipojené')}</span>
            </div>
            {data.discovered.map((d) => (
              <div key={d.id} className='flex items-center gap-3 px-3 py-2 border-b border-gray-800/60 last:border-0'>
                <StatusDot status='unknown' />
                <div className='min-w-0 flex-1'>
                  <div className='flex items-center gap-2'>
                    <span className='text-sm text-gray-200 truncate'>{d.hostname || d.id}</span>
                    {d.bootstrap_method && <Badge variant='gray'>{d.bootstrap_method}</Badge>}
                    {d.default_credentials_used && <Badge variant='red'>default creds</Badge>}
                  </div>
                  <div className='text-[11px] text-gray-500 font-mono'>{d.ip}</div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export default TopologyPanel;
